import React, { FunctionComponent, ReactElement, useContext, useCallback } from 'react'
import cx from 'classnames'
import {observer} from 'mobx-react'
import {ARRAY} from '../../core/types/array'
import {MODEL} from '../../core/types/model'
import {NestedRow} from './NestedRow'
import {LabelFormatter, LabelFormatterCmp, ModelConfig} from '../../core/decorators/constants'
import SchemerModel from '../../core/SchemerModel'
import {TableStoreContext, TableStore} from './TableStore'

export interface HeaderValueProps {
  columnKey: string
  row: SchemerModel
  colWidth?: number
  value?: any
  valueType?: any
  isTopLevel?: boolean
  isStickyCol?: boolean
  sortField?: string
  sortDir?: 'asc'|'desc'
  onChangeSort?: (newSortField: string, newSortDir: 'asc'|'desc') => void
}

interface HeaderActualProps {
  row: SchemerModel
  columnKey: string
  label?: LabelFormatter
  isTopLevel?: boolean
}

const HeaderActual: FunctionComponent<HeaderActualProps> = observer(({columnKey, label, isTopLevel, row}) => {
  let className = ''
  let contents : ReactElement|string|undefined

  if (typeof label === 'function') {
    if (label.prototype && label.prototype.isReactComponent) {
      const FormatterCmp = label as LabelFormatterCmp
      className = `formatter_cmp`
      contents = (
        <FormatterCmp
          propertyName={columnKey}
          model={row}
        />
      )
    }
    else {
      const fmt = label as Function // o.O
      contents = fmt(columnKey)
    }
  }
  else {
    contents = label ? label : (isTopLevel ? columnKey : undefined) // Top level header must have a value
    className = 'no_formatter'
  }
  
  return contents ? (
    <div className={`sch_header_cell_value ${className}`}>
      {contents}
    </div>
  ) : null
})

export const HeaderValue: FunctionComponent<HeaderValueProps> = observer(({
  columnKey,
  row,
  value,
  valueType,
  isTopLevel,
  isStickyCol,
  sortField,
  sortDir,
  onChangeSort
}) => {
  const handleSortClick = useCallback(() => {
    const fieldDefaultSort = 'desc' // TODO set in tableConfig
    const newSortDir = sortField === columnKey ? (sortDir === 'asc' ? 'desc' : 'asc') : fieldDefaultSort

    if (onChangeSort) {
      onChangeSort(columnKey, newSortDir)
    }
  }, [columnKey])

  const tableStore: TableStore = useContext(TableStoreContext)

  const fieldConfig = row.getFieldConfig(columnKey)
  const tableConfig = fieldConfig.table
  const label = row.getHeader(columnKey)
  const type = valueType || row.getType(columnKey)
  const isCurrentSort = columnKey === sortField
  let _value = value || row[columnKey]

  const isModelArray = type === ARRAY && row.getSubtype(columnKey) === MODEL

  let contents = (
    <HeaderActual 
      columnKey={columnKey}
      label={label}
      row={row}
      isTopLevel={isTopLevel}
    />
  )

  if (type === MODEL || isModelArray) {
    let _firstVal = isModelArray && _value && _value.length > 0 ? _value[0] : null
    if (!_firstVal) {
      const SubModel = (fieldConfig as ModelConfig).model          
      _firstVal = new SubModel({})
    }

    contents = isTopLevel ? (
      <div className={`sch_header_cell_multi`}>
        {contents}
        <NestedRow 
          isColHeader={true}
          valueRaw={_firstVal}
        />
      </div>
    ) : (
      <>
        {contents}
        <NestedRow 
          isColHeader={true}
          valueRaw={_firstVal}
        />
      </>
    )
  }

  return (
    <>
      {contents}
      {isTopLevel &&
        <div className={`col_header_btns`}>
          {typeof tableConfig === 'object' && tableConfig.canSort && 
            <button 
              type="button"
              className={cx(
                `sch_sort_btn btn btn-icon icon-triangle-l`,
                isCurrentSort && "is_active",
                sortDir === 'asc' ? 'sort_asc' : 'sort_desc'
              )}
              onClick={handleSortClick}
              data-tooltip={isCurrentSort ? `Reverse sort direction` : `Sort by this field`}
            />
          }
          {typeof tableConfig === 'object' && tableConfig.canStick && 
            <button 
              type="button"
              className={`btn btn-icon icon-pushpin ${isStickyCol ? "is_active" : ""}`}
              onClick={() => {tableStore.toggleStickyColumn(columnKey)}}
              data-tooltip={`${isStickyCol ? 'Unstick' : 'Stick'} Column to view`}
            />
          }
        </div>
      }
    </>
  )
})
